import { useTournamentContext } from "../page";
import { useQuery } from "@tanstack/react-query";
import { playersListQueryOptions } from "@/api/playerList";
import { useSelectedTeam } from "@/context/teamContext";
import { playerDataQueryOptions, type PlayerData } from "@/api/player";
import { goalkeeperMatchStatsQueryOptions, type GoalkeeperMatchKpi } from "@/api/ranking";
import { useState, useMemo, useEffect } from "react";
import type { PlayerStats, GoalkeeperStats } from "@/api/ranking";
import ComparisonEngine from "@/components/general/Dashboard/Comparison/ComparisonEngine";
import { ComparisonMatchPerformanceChart } from "@/components/general/Dashboard/Comparison/ComparisonMatchPerformanceChart";
import { Loader } from "@/components/general/common/loader";

const roundToOneDecimal = (value: number): number => Math.round(value * 10) / 10;

const isGoalkeeperRole = (role: string) => role === "GK";

export const TournamentComparisonPage = () => {
    const {
        selectedTournamentId,
        tournaments,
        viewMode,
        setSelectedPlayers: setContextSelectedPlayers,
    } = useTournamentContext();
    const { teamId } = useSelectedTeam();
    const [selectedPlayers, setSelectedPlayers] = useState<(PlayerStats | GoalkeeperStats)[]>([]);

    const { data: playerListData, isLoading: isPlayerListLoading } = useQuery({
        ...playersListQueryOptions(selectedTournamentId, teamId),
        enabled: !!selectedTournamentId && selectedTournamentId > 0,
    });

    const isGoalkeeperA = selectedPlayers.length > 0 && isGoalkeeperRole(selectedPlayers[0].role);
    const isGoalkeeperB = selectedPlayers.length > 1 && isGoalkeeperRole(selectedPlayers[1].role);

    const selectedTournamentObj = useMemo(
        () => tournaments?.find((t) => t.id === selectedTournamentId),
        [tournaments, selectedTournamentId]
    );

    const tournamentMatchIds = useMemo(
        () => selectedTournamentObj?.matches?.map((m) => m.id) ?? [],
        [selectedTournamentObj]
    );

    const matchInfoMap = useMemo(() => {
        const map = new Map<
            number,
            { date: string; homeTeam: string; awayTeam: string; homeGoals: number; awayGoals: number }
        >();
        selectedTournamentObj?.matches?.forEach((m) => {
            map.set(m.id, {
                date: m.date ?? "",
                homeTeam: m.homeTeam.fullName,
                awayTeam: m.awayTeam.fullName,
                homeGoals: m.homeGoals,
                awayGoals: m.awayGoals,
            });
        });
        return map;
    }, [selectedTournamentObj]);

    const playerAQuery = useQuery({
        ...playerDataQueryOptions(selectedPlayers[0]?.playerId?.toString() || "", selectedTournamentId || 0, teamId),
        enabled: !isGoalkeeperA && !!selectedTournamentId && selectedTournamentId > 0 && !!selectedPlayers[0]?.playerId,
    });

    const playerBQuery = useQuery({
        ...playerDataQueryOptions(selectedPlayers[1]?.playerId?.toString() || "", selectedTournamentId || 0, teamId),
        enabled: !isGoalkeeperB && !!selectedTournamentId && selectedTournamentId > 0 && !!selectedPlayers[1]?.playerId,
    });

    const goalkeeperAQuery = useQuery({
        ...goalkeeperMatchStatsQueryOptions(selectedPlayers[0]?.playerId || 0, tournamentMatchIds, teamId),
        enabled: isGoalkeeperA && (selectedPlayers[0]?.playerId || 0) > 0 && tournamentMatchIds.length > 0,
    });

    const goalkeeperBQuery = useQuery({
        ...goalkeeperMatchStatsQueryOptions(selectedPlayers[1]?.playerId || 0, tournamentMatchIds, teamId),
        enabled: isGoalkeeperB && (selectedPlayers[1]?.playerId || 0) > 0 && tournamentMatchIds.length > 0,
    });

    const availablePlayers = useMemo((): (PlayerStats | GoalkeeperStats)[] => {
        if (!playerListData?.kpis && !playerListData?.goalkeepersKpis) return [];

        const players: PlayerStats[] = (playerListData.kpis || []).map(
            (player: {
                id: number;
                firstName: string;
                lastName: string;
                role: string[];
                averageKpi: number;
            }): PlayerStats => ({
                playerId: player.id,
                firstName: player.firstName,
                lastName: player.lastName,
                line: 0,
                role: player.role.join("/"),
                pointsDefensive: 0,
                pointsOffensive: 0,
                pointsTeamPlay: 0,
                pointsEffort: 0,
                pointsBonus: 0,
                totalPoints: player.averageKpi,
                matchesPlayed: 0,
                timeOnIce: null,
            })
        );

        const goalkeepers: GoalkeeperStats[] = (playerListData.goalkeepersKpis || []).map(
            (player: {
                id: number;
                firstName: string;
                lastName: string;
                role: string[];
                averageKpi: number;
            }): GoalkeeperStats => ({
                playerId: player.id,
                firstName: player.firstName,
                lastName: player.lastName,
                role: player.role.join("/"),
                pointsGSAx: 0,
                pointsHighDangerShots: 0,
                pointsLowDangerShots: 0,
                pointsLateralPassShots: 0,
                pointsScreenedShots: 0,
                totalPoints: player.averageKpi,
                matchesPlayed: 0,
            })
        );

        return [...players, ...goalkeepers];
    }, [playerListData]);

    const computedStats = useMemo(() => {
        const computeFieldStats = (playerData: PlayerData | undefined, base: PlayerStats): PlayerStats => {
            const kpis = playerData?.kpis ?? [];
            const n = kpis.length;
            if (n === 0) return base;
            const sum = (vals: number[]) => vals.reduce((a, b) => a + b, 0);
            const def = sum(kpis.map((k) => k.defensiveData.points));
            const off = sum(kpis.map((k) => k.offensiveData.points));
            const tp = sum(kpis.map((k) => k.teamPlayData.points));
            const eff = sum(kpis.map((k) => k.effortData.points));
            const bonus = sum(kpis.map((k) => k.bonusData.points));
            const total = sum(kpis.map((k) => k.totalPoints));
            if (viewMode === "average") {
                return {
                    ...base,
                    pointsDefensive: roundToOneDecimal(def / n),
                    pointsOffensive: roundToOneDecimal(off / n),
                    pointsTeamPlay: roundToOneDecimal(tp / n),
                    pointsEffort: roundToOneDecimal(eff / n),
                    pointsBonus: roundToOneDecimal(bonus / n),
                    totalPoints: roundToOneDecimal(total / n),
                    matchesPlayed: n,
                };
            }
            return {
                ...base,
                pointsDefensive: roundToOneDecimal(def),
                pointsOffensive: roundToOneDecimal(off),
                pointsTeamPlay: roundToOneDecimal(tp),
                pointsEffort: roundToOneDecimal(eff),
                pointsBonus: roundToOneDecimal(bonus),
                totalPoints: roundToOneDecimal(total),
                matchesPlayed: n,
            };
        };

        const computeGkStats = (kpis: GoalkeeperMatchKpi[] | undefined, base: GoalkeeperStats): GoalkeeperStats => {
            if (!kpis || kpis.length === 0) return base;
            const n = kpis.length;
            const sum = (vals: number[]) => vals.reduce((a, b) => a + b, 0);
            const gsax = sum(kpis.map((k) => k.pointsGSAx));
            const hd = sum(kpis.map((k) => k.pointsHighDangerShots));
            const ld = sum(kpis.map((k) => k.pointsLowDangerShots));
            const lp = sum(kpis.map((k) => k.pointsLateralPassShots));
            const sc = sum(kpis.map((k) => k.pointsScreenedShots));
            const total = sum(kpis.map((k) => k.totalPoints));
            if (viewMode === "average") {
                return {
                    ...base,
                    pointsGSAx: roundToOneDecimal(gsax / n),
                    pointsHighDangerShots: roundToOneDecimal(hd / n),
                    pointsLowDangerShots: roundToOneDecimal(ld / n),
                    pointsLateralPassShots: roundToOneDecimal(lp / n),
                    pointsScreenedShots: roundToOneDecimal(sc / n),
                    totalPoints: roundToOneDecimal(total / n),
                    matchesPlayed: n,
                };
            }
            return {
                ...base,
                pointsGSAx: roundToOneDecimal(gsax),
                pointsHighDangerShots: roundToOneDecimal(hd),
                pointsLowDangerShots: roundToOneDecimal(ld),
                pointsLateralPassShots: roundToOneDecimal(lp),
                pointsScreenedShots: roundToOneDecimal(sc),
                totalPoints: roundToOneDecimal(total),
                matchesPlayed: n,
            };
        };

        return selectedPlayers.map((player, index) => {
            if (isGoalkeeperRole(player.role)) {
                const gkData = index === 0 ? goalkeeperAQuery.data : goalkeeperBQuery.data;
                return computeGkStats(gkData, player as GoalkeeperStats);
            }
            const fieldData = index === 0 ? playerAQuery.data : playerBQuery.data;
            return computeFieldStats(fieldData, player as PlayerStats);
        });
    }, [selectedPlayers, playerAQuery.data, playerBQuery.data, goalkeeperAQuery.data, goalkeeperBQuery.data, viewMode]);

    useEffect(() => {
        const contextPlayers = selectedPlayers.map((player) => ({
            playerId: player.playerId,
            firstName: player.firstName,
            lastName: player.lastName,
        }));
        setContextSelectedPlayers(contextPlayers);
    }, [selectedPlayers, setContextSelectedPlayers]);

    const transformGkDataForChart = (kpis: GoalkeeperMatchKpi[] | undefined) => {
        if (!kpis || kpis.length === 0) return undefined;
        const mappedKpis = kpis
            .map((kpi) => {
                const info = matchInfoMap.get(kpi.matchId);
                if (!info) return null;
                return {
                    matchDate: info.date,
                    homeTeam: info.homeTeam,
                    awayTeam: info.awayTeam,
                    homeGoals: info.homeGoals,
                    awayGoals: info.awayGoals,
                    totalPoints: kpi.totalPoints,
                    gsaxData: { points: kpi.pointsGSAx },
                    highDangerData: { points: kpi.pointsHighDangerShots },
                    lowDangerData: { points: kpi.pointsLowDangerShots },
                    lateralPassData: { points: kpi.pointsLateralPassShots },
                    screenedData: { points: kpi.pointsScreenedShots },
                    role: "GK",
                };
            })
            .filter((item): item is NonNullable<typeof item> => item !== null);
        return { kpis: mappedKpis };
    };

    const [playerAName, playerBName] = useMemo(
        () => [
            computedStats[0] ? `${computedStats[0].firstName} ${computedStats[0].lastName}` : "",
            computedStats[1] ? `${computedStats[1].firstName} ${computedStats[1].lastName}` : "",
        ],
        [computedStats]
    );

    const isPlayerDataLoading =
        playerAQuery.isLoading || playerBQuery.isLoading || goalkeeperAQuery.isLoading || goalkeeperBQuery.isLoading;
    const isAnyLoading = tournaments === undefined || isPlayerListLoading;

    if (isAnyLoading) {
        return (
            <div className="flex flex-col min-h-0 h-[755px]">
                <div className="flex flex-col gap-4 flex-1 min-h-0">
                    <div className="glass card rounded-xl p-6 flex items-center justify-center flex-1">
                        <Loader message="Loading comparison engine..." />
                    </div>

                    <div className="glass card rounded-xl p-6 flex items-center justify-center flex-1">
                        <Loader message="Loading match performance..." />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <section className="flex flex-col h-full overflow-hidden">
            <div className="flex flex-1 flex-col gap-4 overflow-hidden h-full">
                <div
                    id="onboarding-compare-engine"
                    className="flex-1 flex flex-col gap-4 p-3 border border-table-border rounded-md h-full bg-comparison-bg min-h-0"
                >
                    {availablePlayers.length === 0 ? (
                        <div className="flex items-center justify-center h-full">
                            <span className="text-center p-4 text-on-surface-variant">
                                No players available for comparison
                            </span>
                        </div>
                    ) : (
                        <ComparisonEngine
                            data={computedStats}
                            availablePlayersForSelection={availablePlayers}
                            isLoading={isPlayerListLoading}
                            selectedPlayers={computedStats}
                            viewMode={viewMode}
                            setSelectedPlayers={(players) => {
                                if (typeof players === "function") {
                                    setSelectedPlayers(players);
                                } else {
                                    setSelectedPlayers(players);
                                }
                            }}
                        />
                    )}
                </div>
                <div id="onboarding-compare-performance">
                    <ComparisonMatchPerformanceChart
                        playerAData={isGoalkeeperA ? transformGkDataForChart(goalkeeperAQuery.data) : playerAQuery.data}
                        playerBData={isGoalkeeperB ? transformGkDataForChart(goalkeeperBQuery.data) : playerBQuery.data}
                        playerAName={playerAName}
                        playerBName={playerBName}
                        isLoading={isPlayerDataLoading}
                    />
                </div>
            </div>
        </section>
    );
};
